import React, { useEffect, useState } from 'react';
import { AlertNotification } from '@arrow-ds/react';
import './FTAResults.scss';
import { FtaInputs, FtaResult, ProductNames } from '../fta.interface';

interface FTAResultsProps {
  ftaInputs: FtaInputs;
  calculatorData: any;
}

const FTAResults: React.FC<FTAResultsProps> = ({ ftaInputs, calculatorData }) => {
  const [ftaResult, setFtaResult] = useState<FtaResult>();


  const getCleanedValue = (value: string | undefined): string => (value ? value.replace(/\D/g, '') : '');

  const formatFinalCalculatedResult = (amount: any) => {
    const numberAmount = parseFloat(amount);
    if (!isNaN(numberAmount)) {
      if (numberAmount % 1 === 0) {
        return numberAmount.toLocaleString();
      }
      return numberAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return amount;
  };

  const getResultFromCSV = (product: string | undefined, investmentValue: number, investmentTermYears: number) => {
    const selectedData = calculatorData.find((item: any) => (
      ProductNames[item['Product Option']] === product &&
      parseInt(getCleanedValue(item.Investment)) === investmentValue &&
      item.Term === investmentTermYears.toString()
    ));
    return selectedData ? formatFinalCalculatedResult(selectedData.Result) : '£0';
  };

  const calculateResults = (
    product: string | undefined,
    investmentValue: number,
    investmentTermYears: number
  ) => {
    let annualIncomeAmount = '£0';
    let finalPaymentMethod = '£0';
    const calculatedResult = `£${getResultFromCSV(product, investmentValue, investmentTermYears)}`;
    switch (product) {
      case 'Maximum Lump Sum Option':
        finalPaymentMethod = calculatedResult;
        break;
      case 'Maximum Income Option':
        annualIncomeAmount = calculatedResult;
        break;
      case 'Combination Option':
        finalPaymentMethod = `£${investmentValue.toLocaleString()}`;
        annualIncomeAmount = calculatedResult;
        break;
      default:
        break;
    }

    return { annualIncomeAmount, finalPaymentMethod };
  };

  useEffect(() => {
    const investmentValue = parseInt(getCleanedValue(ftaInputs?.investmentAmount));
    const investmentTermYears = parseInt(getCleanedValue(ftaInputs?.investmentTerm));

    const { annualIncomeAmount, finalPaymentMethod } = calculateResults(
      ftaInputs?.product,
      investmentValue,
      investmentTermYears
    );

    setFtaResult({ annualIncomeAmount, finalPaymentMethod });
  }, [ftaInputs]);

  const isResultAvailable = ftaInputs?.investmentAmount && ftaInputs?.investmentTerm && ftaInputs?.product;
  return (
    <div className="root resultData">
      <table className="resultsTable">
        <thead>
          <tr>
            <th className="3 title">Your results</th>
          </tr>
        </thead>
        {isResultAvailable ?
          (
            <tbody>
              <tr>
                <td>Guaranteed Income</td>
                <td className="outputResult">{ftaResult?.annualIncomeAmount} each year</td>
              </tr>
              <tr>
                <td>Guaranteed Lump Sum</td>
                <td className="outputResult">{ftaResult?.finalPaymentMethod}</td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <p className="notes"><b>Important notes</b>
                    <ul>
                      <li>The results shown are not a guaranteed quotation.</li>
                      <li>The results do not include any adviser charges being taken.</li>
                      <li>The results are calculated assuming any income is payable on a monthly in advance basis.</li>
                      <li>The investment amount will be provided from a decumulation pot within an HMRC registered pension scheme.</li>
                      <li>The plan will be set-up as an investment of that scheme.</li>
                    </ul>
                  </p>
                </td>
              </tr>
            </tbody>
          ) :
          (
            <tbody>
              <tr>
                <div className="warningAlert">
                  <AlertNotification
                    actionLabel=""
                    notificationStyle="warning"
                    notificationType="in-page"
                    showDismissButton={false}
                  >
                    Please complete all the fields to see your results here.
                  </AlertNotification>
                </div>
              </tr>
            </tbody>
          )
        }
      </table>
    </div >
  );
};

export default FTAResults;
